@if (visit) {
  <div class="data-platform-visit-flyout">
    <!-- Condensed sticky header, fades in when the full header scrolls out of view -->
    <div class="mini-header-wrap">
      <div class="mini-header" [class.mini-header--visible]="showMiniHeader()">
        <app-person-avatar [email]="avatarEmail" [diameter]="18"></app-person-avatar>
        @if (isOngoing()) {
          <span class="mini-header__ongoing-dot" [matTooltip]="'data-platform.ongoing' | translate"></span>
        }
        <span class="mini-header__title">{{ headerTitle }}</span>
        <span class="mini-header__score mini-header__score--{{ tier }}">
          <mat-icon class="mini-header__score-icon">{{ tierIcon }}</mat-icon>
          {{ visit.engagementScore }}
        </span>
        <mat-icon class="mini-header__close" (click)="closeFlyout()">close</mat-icon>
      </div>
    </div>

    <!-- Back to profile, shown when this visit was opened from a profile flyout -->
    @if (backTitle) {
      <div class="back-crumb-bar">
        <button type="button" class="back-crumb" (click)="openProfile()">
          <mat-icon class="back-crumb__icon">arrow_back</mat-icon>
          <span>{{ "data-platform.back-to-profile" | translate }}: {{ backTitle }}</span>
        </button>
      </div>
    }

    <!-- Header -->
    <div class="flyout-header" #flyoutHeaderEl>
      <div class="flyout-header-left">
        <app-person-avatar [email]="avatarEmail" [diameter]="32"></app-person-avatar>
        <div class="header-text">
          <div class="header-title-row">
            <div class="header-title">{{ headerTitle }}</div>
            @if (isOngoing()) {
              <span class="ongoing-badge" [matTooltip]="'data-platform.ongoing-hint' | translate">
                <span class="ongoing-badge__dot"></span>
                {{ "data-platform.ongoing" | translate }}
              </span>
            }
            @if (privacyTooltip) {
              <mat-icon
                fontSet="material-symbols-outlined"
                class="header-privacy-icon"
                [class.header-privacy-icon--warning]="privacyIsWarning"
                [matTooltip]="privacyTooltip"
                matTooltipClass="long-value-tooltip">
                cookie_off
              </mat-icon>
            }
          </div>
          @if (headerSubtitle) {
            <div class="header-subtitle">{{ headerSubtitle }}</div>
          }
        </div>
      </div>
      <div class="flyout-header-actions">
        @if (showVisitNav) {
          <button
            type="button"
            class="visit-nav-btn"
            [disabled]="!hasNewerVisit"
            [matTooltip]="'data-platform.newer-visit' | translate"
            (click)="openNewerVisit()">
            <mat-icon>chevron_left</mat-icon>
          </button>
          <button
            type="button"
            class="visit-nav-btn"
            [disabled]="!hasOlderVisit"
            [matTooltip]="'data-platform.older-visit' | translate"
            (click)="openOlderVisit()">
            <mat-icon>chevron_right</mat-icon>
          </button>
        }
        <!-- On the wrapper: a disabled button raises no hover, so its own tooltip would never show. -->
        <span
          class="view-profile-wrapper"
          [matTooltip]="'data-platform.view-profile-cookie-free' | translate"
          [matTooltipDisabled]="hasProfile">
          <button type="button" class="view-profile-btn" [disabled]="!hasProfile" (click)="openProfile()">
            <mat-icon class="view-profile-btn-icon">person</mat-icon>
            <span>{{ "data-platform.view-profile" | translate }}</span>
          </button>
        </span>
        <mat-icon class="close-icon" (click)="closeFlyout()">close</mat-icon>
      </div>
    </div>

    <!-- Intent Score Card -->
    <div class="score-card score-card--{{ tier }}">
      <div class="score-card-top">
        <span class="score-card-label-group">
          <span class="score-card-label">{{ "data-platform.engagement-score" | translate }}</span>
          <mat-icon
            class="score-card-info"
            tabindex="0"
            aria-hidden="false"
            [attr.aria-label]="'data-platform.engagement-visit-score-info' | translate"
            [matTooltip]="'data-platform.engagement-visit-score-info' | translate"
            matTooltipClass="multiline-tooltip long-value-tooltip"
            data-test="visit-score-info">
            info_outline
          </mat-icon>
        </span>
        <span class="score-card-badge">
          <mat-icon class="score-card-badge-icon">{{ tierIcon }}</mat-icon>
          {{ tierLabelKey | translate }}
        </span>
      </div>
      <div class="score-card-body">
        <span class="score-card-number">{{ visit.engagementScore }}</span>
        <div class="score-card-track">
          <div class="score-card-fill" [style.width.%]="visit.engagementScore"></div>
        </div>
      </div>
    </div>

    <!-- Audience Insights -->
    @if (matchedInsights.length > 0) {
      <div class="chips-label">{{ "data-platform.audiences" | translate }}</div>
      <div class="insight-chips">
        @for (insight of matchedInsights; track insight.id) {
          <span class="insight-chip insight-chip--{{ insight.variant }}">
            <mat-icon class="insight-chip-icon">{{ insight.icon }}</mat-icon>
            {{ insight.label }}
          </span>
        }
      </div>
    }

    <!-- General Info -->
    <div class="flyout-row">
      <div class="flyout-data-group">
        <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
          <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
            <img class="header-icon" src="/assets/img/icons_fl/user-analytics.svg" width="32px" height="32px" />
            <span>{{ "data-platform.general" | translate }}</span>
          </mat-expansion-panel-header>

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.when" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ startedAtDate | formatDate }}</span>
            </div>
          </div>

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ (isOngoing() ? "data-platform.last-activity" : "data-platform.ended") | translate }}</span>
            </div>
            <div class="value">
              <span>{{ endedAtDate() | formatDate }}</span>
            </div>
          </div>

          @if (loginStatusKey) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.login-status" | translate }}</span>
              </div>
              <div class="value">
                <span>{{ loginStatusKey | translate }}</span>
              </div>
            </div>
          }

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.pages" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ pageViewCount | toLocaleNumber }}</span>
            </div>
          </div>

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.goals" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ goalCount | toLocaleNumber }}</span>
            </div>
          </div>

          @if (totalRevenue) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.revenue" | translate }}</span>
              </div>
              <div class="value">
                <span>{{ totalRevenue | formatCurrency: currency }}</span>
              </div>
            </div>
          }

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.duration" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ sessionDurationSeconds() | visitDuration }}</span>
              @if (isOngoing()) {
                <span class="value-note">{{ "data-platform.duration-so-far" | translate }}</span>
              }
            </div>
          </div>

          @if (organization) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "flyout.general.organisation" | translate }}</span>
              </div>
              <div class="value">
                <span>{{ organization }}</span>
              </div>
            </div>
          }

          @if (visit.geoCountry) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "flyout.general.country" | translate }}</span>
              </div>
              <div class="value">
                <span class="flag-{{ visit.geoCountry | lowercase }}"></span>
                <span>{{ visit.geoCountry }}</span>
              </div>
            </div>
          }

          @if (hasHoldout) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.holdout-group" | translate }}</span>
              </div>
              <div class="value">
                <span>{{ visit.isHoldout ? ("data-platform.holdout-yes" | translate) : ("data-platform.holdout-no" | translate) }}</span>
              </div>
            </div>
          }

          @if (allTags?.length > 0) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.tags" | translate }}</span>
              </div>
              <div class="value tags-value">
                @for (tag of allTags; track tag) {
                  <span class="tag-chip">{{ tag }}</span>
                }
              </div>
            </div>
          }

          @if (isOngoing() && !visit.browser && !visit.os) {
            <div class="expansion-panel-row no-border">
              <span class="finalization-hint">{{ "data-platform.ongoing-details-hint" | translate }}</span>
            </div>
          }
        </mat-expansion-panel>
      </div>
    </div>

    <!-- Dimensions Affinity -->
    <div class="flyout-row">
      <div class="flyout-data-group">
        <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
          <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
            <mat-icon class="section-icon">category</mat-icon>
            <span>{{ "data-platform.dimensions" | translate }}</span>
          </mat-expansion-panel-header>

          @if (dimensionTypeDonut?.length === 0) {
            <div class="expansion-panel-row no-border interests-empty">
              <span>{{ "data-platform.no-interest-data" | translate }}</span>
            </div>
          } @else {
            @for (group of dimensionTypeDonut; track group.type) {
              @let segments = group.showAll ? group.allSegments : group.segments;
              <div class="dim-type-section">
                <div class="dim-type-label">{{ group.displayType }}</div>
                <div class="dim-affinity-panel">
                  <svg viewBox="0 0 40 40" class="dim-donut" aria-hidden="true">
                    <circle cx="20" cy="20" r="15.9155" fill="transparent" stroke="var(--cardDivider)" stroke-width="4" />
                    @for (seg of segments; track $index) {
                      <circle
                        cx="20"
                        cy="20"
                        r="15.9155"
                        fill="transparent"
                        [attr.stroke]="seg.color"
                        stroke-width="4"
                        [attr.stroke-dasharray]="seg.dashArray"
                        [attr.stroke-dashoffset]="seg.dashOffset"
                        [matTooltip]="
                          'data-platform.dimension-legend-tooltip' | translate: { dimension: seg.dimension, count: seg.count, percentage: seg.percentage }
                        "></circle>
                    }
                  </svg>
                  <ul class="dim-legend">
                    @for (seg of segments; track $index) {
                      <li class="dim-legend-item" [matTooltip]="seg.membersTooltip">
                        <span class="dim-legend-dot" [style.background]="seg.color"></span>
                        <span class="dim-legend-label">{{ seg.dimension }}</span>
                        <span class="dim-legend-pct">{{ seg.percentage }}%</span>
                        <span class="dim-legend-count">({{ seg.count | toLocaleNumber }})</span>
                      </li>
                    }
                  </ul>
                </div>
                @if (group.hiddenCount > 0) {
                  <button type="button" class="dim-show-more" (click)="group.showAll = !group.showAll">
                    {{ (group.showAll ? "data-platform.dimension-show-less" : "data-platform.dimension-show-more") | translate: { count: group.hiddenCount } }}
                  </button>
                }
              </div>
            }
          }
        </mat-expansion-panel>
      </div>
    </div>

    <!-- Distributed Coupons -->
    @if (visit.distributedCoupons?.length > 0 || couponShown || couponCopied) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">local_offer</mat-icon>
              <span>
                {{ "data-platform.distributed-coupons" | translate }}
                @if (visit.distributedCoupons?.length > 0) {
                  ({{ visit.distributedCoupons.length }})
                }
              </span>
            </mat-expansion-panel-header>

            @if (!visit.distributedCoupons?.length) {
              <div class="expansion-panel-row no-border coupon-row">
                <div class="coupon-row__code">
                  <span>{{ "data-platform.coupon-shown" | translate }}</span>
                </div>
                <div class="value coupon-row__state">
                  @if (couponCopied) {
                    <span class="coupon-status coupon-status--copied">
                      <mat-icon class="coupon-status__icon">check_circle</mat-icon>
                      {{ "data-platform.coupon-copied" | translate }}
                    </span>
                  } @else {
                    <span class="coupon-status coupon-status--not-copied">
                      <mat-icon class="coupon-status__icon">highlight_off</mat-icon>
                      {{ "data-platform.coupon-not-copied" | translate }}
                    </span>
                  }
                </div>
              </div>
            }

            @for (coupon of visit.distributedCoupons; track $index) {
              <div class="expansion-panel-row no-border coupon-row">
                <div class="coupon-row__code">
                  @if (coupon.code) {
                    <span>{{ coupon.code }}</span>
                  } @else {
                    <span class="coupon-row__placeholder">{{ "data-platform.coupon-code-placeholder" | translate }}</span>
                  }
                </div>
                <div class="value coupon-row__state">
                  @if (coupon.isCopied) {
                    <span class="coupon-status coupon-status--copied">
                      <mat-icon class="coupon-status__icon">check_circle</mat-icon>
                      {{ "data-platform.coupon-copied" | translate }}
                    </span>
                  } @else {
                    <span class="coupon-status coupon-status--not-copied">
                      <mat-icon class="coupon-status__icon">highlight_off</mat-icon>
                      {{ "data-platform.coupon-not-copied" | translate }}
                    </span>
                  }
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Traits -->
    @if (traitsEntries().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">person_pin</mat-icon>
              <span>{{ "data-platform.properties" | translate }}</span>
            </mat-expansion-panel-header>

            @for (trait of traitsEntries(); track trait.key) {
              <div class="expansion-panel-row values trait-row" [class.trait-row--identifier]="trait.isIdentifierValue">
                <div class="label">
                  <span [title]="trait.key">{{ trait.label }}</span>
                </div>
                <div class="value">
                  <span [attr.title]="trait.isIdentifierValue ? trait.value : null">{{ trait.value }}</span>
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Consent Events -->
    <app-data-platform-consent-list [consentEvents]="consentEvents"></app-data-platform-consent-list>

    <!-- Purchases -->
    <app-data-platform-purchases-list [purchases]="purchaseItems" [totalRevenue]="totalRevenue" [currency]="currency"></app-data-platform-purchases-list>

    <!-- Credited to -->
    @if (creditViews().length > 0) {
      <div class="flyout-row" data-test="visit-credits">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">layers</mat-icon>
              <span>{{ "data-platform.credited-to" | translate }}</span>
              <mat-icon class="admin-only-icon">font_download</mat-icon>
            </mat-expansion-panel-header>

            @for (credit of creditViews(); track $index) {
              <div class="expansion-panel-row no-border credit-row">
                <ng-container *ngTemplateOutlet="widgetThumbnail; context: { $implicit: credit.thumbnail }"></ng-container>
                <div class="credit-row__text">
                  <span class="credit-row__name" [matTooltip]="credit.name" matTooltipClass="long-value-tooltip" appTooltipWhenTruncated>
                    {{ credit.name }}
                  </span>
                  <span class="credit-row__type">{{ credit.typeLabelKey | translate }}</span>
                </div>
                <span class="credit-row__amount">{{ credit.revenue | formatCurrency: currency }}</span>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Events Timeline -->
    @if (isLoadingEvents() || eventViews().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">timeline</mat-icon>
              <span>
                {{ "data-platform.events" | translate }}
                @if (!isLoadingEvents()) {
                  ({{ eventViews().length }})
                }
              </span>
            </mat-expansion-panel-header>

            @if (isLoadingEvents()) {
              <div class="section-skeleton">
                @for (width of [60, 45, 70, 50]; track $index) {
                  <div class="section-skeleton__row">
                    <span class="section-skeleton__cell" [style.width.%]="width"></span>
                    <span class="section-skeleton__cell section-skeleton__cell--end"></span>
                  </div>
                }
              </div>
            } @else {
              @if (importantEventCount() > 0 && importantEventCount() < eventViews().length) {
                <div class="event-filter" role="tablist">
                  <button
                    type="button"
                    class="event-filter__btn"
                    role="tab"
                    [attr.aria-selected]="eventFilter() === 'all'"
                    [class.event-filter__btn--active]="eventFilter() === 'all'"
                    (click)="eventFilter.set('all')">
                    {{ "data-platform.events-all" | translate }} ({{ eventViews().length }})
                  </button>
                  <button
                    type="button"
                    class="event-filter__btn"
                    role="tab"
                    [attr.aria-selected]="eventFilter() === 'important'"
                    [class.event-filter__btn--active]="eventFilter() === 'important'"
                    (click)="eventFilter.set('important')">
                    {{ "data-platform.events-important" | translate }} ({{ importantEventCount() }})
                  </button>
                </div>
              }

              <div class="events-timeline">
                @for (event of visibleEvents(); track event.timestamp; let last = $last) {
                  <div class="timeline-item" [class.timeline-item--last]="last">
                    <div class="timeline-connector">
                      <div class="timeline-dot" [class.timeline-dot--goal]="event.goalName">
                        <mat-icon class="timeline-icon">{{ event.icon }}</mat-icon>
                      </div>
                      @if (!last) {
                        <div class="timeline-line"></div>
                      }
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header">
                        <span class="timeline-type">{{ event.typeLabel }}</span>
                        @for (dim of event.dimensionLabels; track dim) {
                          <span class="timeline-category">{{ dim }}</span>
                        }
                        <span class="timeline-time">{{ event.timestampDate | formatDate }}</span>
                      </div>
                      @if (event.widgetLabel) {
                        <div class="timeline-widget">
                          <ng-container *ngTemplateOutlet="widgetThumbnail; context: { $implicit: event.widgetThumbnail }"></ng-container>
                          <span class="timeline-widget__name" [matTooltip]="event.widgetLabel" matTooltipClass="long-value-tooltip" appTooltipWhenTruncated>
                            {{ event.widgetLabel }}
                          </span>
                        </div>
                      } @else {
                        <div class="timeline-label" [matTooltip]="event.label" matTooltipClass="long-value-tooltip" appTooltipWhenTruncated>
                          {{ event.label }}
                        </div>
                      }
                      @if (event.actionUrl && event.actionUrl !== event.label) {
                        <div class="timeline-url" [matTooltip]="event.actionUrl" matTooltipClass="long-value-tooltip" appTooltipWhenTruncated>
                          {{ event.actionUrl }}
                        </div>
                      }
                      @if (event.revenue) {
                        <span class="goal-revenue">{{ event.revenue | formatCurrency: currency }}</span>
                      }
                      @if (event.tags?.length > 0) {
                        <div class="timeline-tags">
                          @for (tag of event.tags; track tag) {
                            <span class="tag-chip tag-chip--small">{{ tag }}</span>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Device Info -->
    @if (visit.browser || visit.os) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <img class="header-icon" src="/assets/img/icons_fl/devices.svg" width="32px" height="32px" />
              <span>{{ "flyout.device.title" | translate }}</span>
            </mat-expansion-panel-header>

            @if (visit.browser) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.device.webBrowser" | translate }}</span>
                </div>
                <div class="value">
                  <app-browser-icon [browser]="visit.browser"></app-browser-icon>
                  <span>{{ visit.browser }}</span>
                </div>
              </div>
            }

            @if (visit.os) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.device.os" | translate }}</span>
                </div>
                <div class="value">
                  <app-os-icon [os]="visit.os"></app-os-icon>
                  <span>{{ visit.os }}</span>
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Source / Campaign -->
    @if (visit.sessionSource || visit.referrerApp || visit.referrerDomain || visit.referrerType || visit.utmCampaignName || visit.utmCampaignSource) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <img class="header-icon" src="/assets/img/icons_fl/source.svg" width="32px" height="32px" />
              <span>{{ "flyout.source.title" | translate }}</span>
            </mat-expansion-panel-header>

            @if (visit.sessionSource) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.session-source" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ "data-platform.session-source-values." + visit.sessionSource | translate }}</span>
                </div>
              </div>
            }

            @if (visit.referrerApp) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.referrer-app" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ "data-platform.referrer-app-values." + visit.referrerApp | translate }}</span>
                </div>
              </div>
            }

            @if (visit.referrerType) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.referer-type" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ visit.referrerType }}</span>
                </div>
              </div>
            }

            @if (visit.referrerDomain) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.referer" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ visit.referrerDomain }}</span>
                </div>
              </div>
            }

            @if (visit.utmCampaignName) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.campaign" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ visit.utmCampaignName }}</span>
                </div>
              </div>
            }

            @if (visit.utmCampaignSource) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.utm-source" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ visit.utmCampaignSource }}</span>
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }
  </div>
}

<ng-template #widgetThumbnail let-src>
  @if (src) {
    <img class="widget-thumbnail" [src]="src" alt="" loading="lazy" (error)="onThumbnailError(src)" />
  } @else {
    <span class="widget-thumbnail widget-thumbnail--placeholder" aria-hidden="true">
      <mat-icon>image</mat-icon>
    </span>
  }
</ng-template>
